import React from 'react';
import { Switch } from 'antd';
import booleanToText from '../../utils/booleanToText';
import formatDateTime from '../../utils/formatDateTime';
import ListItem from '../../components/shared/ListItem';
import CarvalooStatusBadge from '../shared/CarvalooStatusBadge';
import CopyableLinkRow from '../shared/CopyableLinkRow';
import ReportedByLine from './ReportedByLine';
import { OutputDamageData } from '../../@types';
import { ImageStyles } from '../TimeLine/ImageAdded.styles';
import { claimInfoStyles } from './ClaimInfo.styles';
import ClaimStatusItem from '../TimeLine/ClaimStatusItem';
import ClaimStatusLabel from '../../components/shared/ClaimStatusLabel';
import { useIntl } from 'react-intl';
import ImageAdded from '../TimeLine/ImageAdded';
import { colors } from '../../theme/theme';
import { batch, useDispatch } from 'react-redux';
import { selectClaim } from '../../redux/store/userData/claims/actions';
import { updateClaimIfNeeded } from '../../redux/store/userData/claims';
import ListEditItem from '../shared/ListEditItem';
import {
  EditingClaimSource,
  openModal,
  setEditingClaim,
} from '../../redux/store/ui/common';

const IMAGE_PATH = '/images/timeline';

type ClaimInfoProps = {
  claim: OutputDamageData;
  index?: number;
  source?: EditingClaimSource;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ClaimInfo: React.FC<ClaimInfoProps> = ({
  claim,
  index,
  source = 'claim-drawer',
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const roadworthiness = booleanToText(claim?.carRoadworthy!);
  const thirdPartyInvolved = booleanToText(claim?.thirdPartyInvolved!);

  const isNoImages = !claim.damageImages || claim.damageImages.length === 0;

  const carvaloo: any = claim?.carvaloo;
  const reportedByCarvaloo = (claim as any)?.reportedByCarvaloo;
  const damageEvents: any[] = (claim as any)?.events || [];
  const feedbackEvent = damageEvents.find((e) => e?.type === 'feedback');
  const reportedAt = claim.addedAt;
  const feedbackedAt =
    feedbackEvent?.created ||
    carvaloo?.feedback?.created ||
    carvaloo?.claims?.created;
  const feedbackInfo = feedbackEvent?.comments;
  // Always use our S3 copy — carvaloo's blob URLs have short-lived SAS tokens.
  const feedbackImages: string[] = claim?.damageImages || [];
  const carvalooFeedbackUrl: string | undefined = carvaloo?.feedbackUrl;
  const carvalooPdfUrl: string | undefined =
    claim?.carvalooPdfReportUrl || carvaloo?.pdfReportUrl;
  const hiddenClaim =
    claim?.hiddenFromUser ||
    (claim?.status === 'closed' && claim?.closeReason === 'declined');

  const onClickDamageId = () => {
    batch(() => {
      dispatch(selectClaim(claim));
    });
  };

  const onEditDamage = () => {
    batch(() => {
      dispatch(setEditingClaim({ claim, source }));
      dispatch(openModal('ClaimDamageModal'));
    });
  };

  const onClickAddImage = () => {
    batch(() => {
      dispatch(setEditingClaim({ claim, source }));
      dispatch(openModal('ClaimImageAddModal'));
    });
  };

  const onToggleHidden = (checked: boolean) => {
    if (!claim?.id) return;
    dispatch(updateClaimIfNeeded(claim.id, { hiddenFromUser: checked }));
  };

  return (
    <div
      style={{
        width: 0,
        minWidth: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      <div>
        <div style={{ marginTop: -16, paddingBottom: '20px' }}>
          <ClaimStatusItem
            title={intl.formatMessage({
              id: 'fleetadmin.claim.section.reported',
            })}
            date={formatDateTime(reportedAt)}
            icon={`${IMAGE_PATH}/redDot.svg`}
            body={
              <>
                <ReportedByLine
                  reportedByCarvaloo={(claim as any)?.reportedByCarvaloo}
                  userReferralCode={claim.userReferralCode}
                  geoLocation={claim.geoLocation}
                />
                {carvaloo?.eventStatus && (
                  <ListItem
                    testid="ClaimInfo-carvalooEventStatus"
                    style={{ margin: 0, marginBottom: '10px' }}
                    label={intl.formatMessage({
                      id: 'fleetadmin.claim.carvaloo.eventStatus',
                      defaultMessage: 'Carvaloo Event Status',
                    })}
                    value={
                      <CarvalooStatusBadge status={carvaloo.eventStatus} />
                    }
                  />
                )}
                {claim?.carvalooEventUrl && carvaloo?.eventId && (
                  <CopyableLinkRow
                    testid="ClaimInfo-carvalooEventId"
                    label={intl.formatMessage({
                      id: 'fleetadmin.claim.carvaloo.eventId',
                      defaultMessage: 'Carvaloo Event ID',
                    })}
                    url={claim.carvalooEventUrl}
                    displayText={carvaloo.eventId}
                  />
                )}
                {carvalooFeedbackUrl && (
                  <CopyableLinkRow
                    testid="ClaimInfo-carvalooFeedbackUrl"
                    label={intl.formatMessage({
                      id: 'fleetadmin.claim.carvaloo.feedbackUrl',
                      defaultMessage: 'Feedback URL',
                    })}
                    url={carvalooFeedbackUrl}
                  />
                )}
                <ListItem
                  testid="ClaimInfo-claimId"
                  style={{ margin: 0, marginBottom: '10px', marginTop: '24px' }}
                  label={intl.formatMessage({
                    id: 'fleetadmin.claim.id.label',
                  })}
                  value={
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div
                        onClick={onClickDamageId}
                        style={{ color: colors.blue, cursor: 'pointer' }}
                      >
                        {claim.damageId}
                      </div>
                      <ClaimStatusLabel status={claim?.status || 'open'} />
                    </div>
                  }
                />
                <div className="row">
                  <ListEditItem
                    testid="ClaimInfo-in/exLabel"
                    style={{
                      flex: 1,
                      margin: 0,
                      marginBottom: '10px',
                      marginRight: '12px',
                    }}
                    label={intl.formatMessage({
                      id: 'fleetadmin.claim.where.label',
                    })}
                    leftItem={
                      claim?.where
                        ? intl.formatMessage({
                            id: `fleetadmin.damage.where.${claim.where}`,
                          })
                        : '-'
                    }
                    onClickEdit={onEditDamage}
                  />
                  <ListEditItem
                    testid="ClaimInfo-locationLabel"
                    style={{ flex: 1, margin: 0, marginBottom: '10px' }}
                    label={intl.formatMessage({
                      id: 'fleetadmin.claim.location.label',
                    })}
                    leftItem={
                      claim?.location
                        ? intl.formatMessage({
                            id: `fleetadmin.damage.location.${claim.location}`,
                          })
                        : '-'
                    }
                    onClickEdit={onEditDamage}
                  />
                </div>
                <div className="row">
                  <ListEditItem
                    testid="ClaimInfo-sideLabel"
                    style={{
                      flex: 1,
                      margin: 0,
                      marginBottom: '10px',
                      marginRight: '12px',
                    }}
                    label={intl.formatMessage({
                      id: 'fleetadmin.claim.side.label',
                    })}
                    leftItem={
                      claim?.side
                        ? intl.formatMessage({
                            id: `fleetadmin.damage.side.${claim.side}`,
                          })
                        : '-'
                    }
                    onClickEdit={onEditDamage}
                  />
                  <ListEditItem
                    testid="ClaimInfo-componentLabel"
                    style={{ flex: 1, margin: 0, marginBottom: '10px' }}
                    label={intl.formatMessage({
                      id: 'fleetadmin.claim.component.label',
                    })}
                    leftItem={
                      claim?.component
                        ? intl.formatMessage({
                            id: `fleetadmin.damage.component.${claim.component}`,
                          })
                        : '-'
                    }
                    onClickEdit={onEditDamage}
                  />
                </div>
                <div className="row">
                  <ListItem
                    style={{
                      flex: 1,
                      margin: 0,
                      marginBottom: '10px',
                      marginRight: '12px',
                    }}
                    label={intl.formatMessage({
                      id: 'fleetadmin.claim.roadworthiness.label',
                    })}
                    value={roadworthiness}
                  />
                  <ListItem
                    style={{ flex: 1, margin: 0, marginBottom: '10px' }}
                    label={intl.formatMessage({
                      id: 'fleetadmin.claim.3rdparty.label',
                    })}
                    value={thirdPartyInvolved}
                  />
                </div>
                <ListEditItem
                  testid="ClaimInfo-description"
                  style={{ margin: 0, marginBottom: '10px' }}
                  label={intl.formatMessage({
                    id: 'fleetadmin.claim.description',
                  })}
                  leftItem={claim.damageDescription || '-'}
                  onClickEdit={onEditDamage}
                />
                {claim.characteristicsFreeText && (
                  <ListEditItem
                    testid="ClaimInfo-characteristicsFreeText"
                    style={{ margin: 0, marginBottom: '10px' }}
                    label={intl.formatMessage({
                      id: 'fleetadmin.claim.characteristicsFreeText',
                    })}
                    leftItem={claim.characteristicsFreeText}
                    onClickEdit={onEditDamage}
                  />
                )}
                <ListItem
                  testid="ClaimInfo-hiddenFromUser"
                  style={{ margin: 0, marginBottom: '10px' }}
                  label={intl.formatMessage({
                    id: 'fleetadmin.claim.hiddenFromUser.label',
                  })}
                  value={
                    <Switch
                      data-testid="ClaimInfo-hiddenFromUser-switch"
                      checked={hiddenClaim}
                      onChange={onToggleHidden}
                    />
                  }
                />
                <div className="row">
                  <ListItem
                    style={{
                      flex: 1,
                      margin: 0,
                      marginBottom: '10px',
                      marginRight: '12px',
                    }}
                    label={intl.formatMessage({
                      id: 'fleetadmin.claim.vehicle.label',
                    })}
                    value={claim.plate}
                  />
                  <ListItem
                    style={{ flex: 1, margin: 0, marginBottom: '10px' }}
                    label={intl.formatMessage({
                      id: 'fleetadmin.claim.mileage.label',
                    })}
                    value={claim.mileage != null && `${claim.mileage} km`}
                  />
                </div>
                {carvalooPdfUrl && (
                  <ListItem
                    style={{ flex: 1, margin: 0, marginBottom: '10px' }}
                    label={intl.formatMessage({
                      id: 'fleetadmin.claim.carvaloo.pdf',
                    })}
                    value={
                      <a
                        style={{ color: colors.blue, cursor: 'pointer' }}
                        href={carvalooPdfUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Link
                      </a>
                    }
                  />
                )}
                <div
                  className="images-wrapper"
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <img
                    data-testid="ClaimInfo-addImageBtn"
                    style={{ width: 18, height: 18, cursor: 'pointer' }}
                    src="/images/drawer/add.svg"
                    alt="add"
                    onClick={onClickAddImage}
                  />
                  {isNoImages ? (
                    <p className="no-img-message" style={{ margin: 0 }}>
                      {intl.formatMessage({
                        id: 'fleetadmin.claim.missing.images',
                      })}
                    </p>
                  ) : (
                    <ImageAdded
                      vehicleEvent={{ ...claim, images: claim.damageImages }}
                      unlimited
                      damageId={claim?.id}
                      imageMeta={claim?.images}
                      source={source}
                    />
                  )}
                </div>
              </>
            }
            iconSize={16}
            hiddenTopBar={true}
            editable={false}
            onPressEdit={() => {}}
          />
          {reportedByCarvaloo && feedbackedAt && (
            <ClaimStatusItem
              title={intl.formatMessage({
                id: 'fleetadmin.claim.section.feedbacked',
                defaultMessage: 'Feedbacked',
              })}
              date={formatDateTime(feedbackedAt)}
              icon={`${IMAGE_PATH}/camera.svg`}
              iconSize={16}
              hiddenTopBar={false}
              editable={false}
              onPressEdit={() => {}}
              body={
                <>
                  <ImageAdded
                    vehicleEvent={{ ...claim, images: feedbackImages }}
                    unlimited
                    damageId={claim?.id}
                    imageMeta={claim?.images}
                    source={source}
                  />
                  <ListItem
                    testid="ClaimInfo-carvalooComment"
                    style={{ margin: 0, marginTop: 10 }}
                    label={intl.formatMessage({
                      id: 'fleetadmin.claim.carvaloo.userComment',
                      defaultMessage: 'Carvaloo user Comment',
                    })}
                    value={feedbackInfo || '-'}
                  />
                </>
              }
            />
          )}
        </div>
        <style jsx>{claimInfoStyles}</style>
        <style jsx>{ImageStyles}</style>
      </div>
    </div>
  );
};

export default ClaimInfo;
